import { useCallback, useMemo, useEffect } from 'react';
import { TargetCostRanges, DynamicRange } from '@/types/target-cost';
import { updateRangeCalculations } from '@/utils/target-cost-calculations';

interface UseRangeCalculationsOptions {
  ranges: TargetCostRanges;
  onChange: (updates: Partial<TargetCostRanges>) => void;
  targetPrice?: number;
  actualCostPct?: number;
}

interface RangeTotals {
  totalAmountInRange: number;
  totalContractorShare: number;
  lessThanAmount: number;
  lessThanShare: number;
  greaterThanAmount: number;
  greaterThanShare: number;
}

interface UseRangeCalculationsReturn {
  totals: RangeTotals;
  handleFixedRangeChange: (
    type: 'less-than' | 'greater-than',
    field: 'percentage' | 'sharePercentage',
    value: string
  ) => void;
  handleDynamicRangeChange: (
    id: string,
    field: keyof Omit<DynamicRange, 'id'>,
    value: string
  ) => void;
  addDynamicRange: () => void;
  removeDynamicRange: (id: string) => void;
}

export function useRangeCalculations({
  ranges,
  onChange,
  targetPrice,
  actualCostPct,
}: UseRangeCalculationsOptions): UseRangeCalculationsReturn {
  // Calculate range values
  const calculateRanges = useCallback(() => {
    if (!targetPrice || actualCostPct === undefined || targetPrice <= 0) return;

    const rangeDefinitions = ranges.dynamicRanges.map((range) => ({
      from: range.startPercentage,
      to: range.endPercentage,
      share: range.sharePercentage,
    }));

    const results = updateRangeCalculations({
      targetPrice,
      actualCostPct,
      ranges: rangeDefinitions,
    });

    const updatedRanges = ranges.dynamicRanges.map((range, index) => ({
      ...range,
      amountInRange: results[index]?.amountInRange || 0,
      contractorShare: results[index]?.contractorShare || 0,
    }));

    onChange({ ...ranges, dynamicRanges: updatedRanges });
  }, [targetPrice, actualCostPct, ranges, onChange]);

  useEffect(() => {
    calculateRanges();
  }, [calculateRanges]);

  // Range sequencing without gaps
  useEffect(() => {
    const { lessThanRange, greaterThanRange, dynamicRanges } = ranges;
    let updatedRanges = [...dynamicRanges];
    let hasChanges = false;

    updatedRanges = updatedRanges.map((range, index) => {
      let start = range.startPercentage;
      let end = range.endPercentage;

      // First dynamic range starts after less-than
      if (index === 0) {
        const expectedStart = lessThanRange.percentage;
        if (start !== expectedStart) {
          start = expectedStart;
          hasChanges = true;
        }
      } else {
        // Subsequent ranges start directly after previous
        const prevEnd = updatedRanges[index - 1].endPercentage;
        if (start !== prevEnd) {
          start = prevEnd;
          hasChanges = true;
        }
      }

      // Ensure valid range width (no overlap but no enforced gap)
      if (end <= start) {
        end = start + 1;
        hasChanges = true;
      }

      return { ...range, startPercentage: start, endPercentage: end };
    });

    // Update greater-than range
    const lastDynamic = updatedRanges[updatedRanges.length - 1];
    if (lastDynamic) {
      const newGTStart = lastDynamic.endPercentage;
      if (greaterThanRange.percentage !== newGTStart) {
        onChange({
          ...ranges,
          dynamicRanges: updatedRanges,
          greaterThanRange: { ...greaterThanRange, percentage: newGTStart },
        });
        return;
      }
    }

    if (hasChanges) {
      onChange({ ...ranges, dynamicRanges: updatedRanges });
    }
  }, [ranges, onChange]);

  // Totals calculation
  const totals = useMemo<RangeTotals>(() => {
    const dynamicTotals = ranges.dynamicRanges.reduce(
      (acc, range) => ({
        totalAmountInRange: acc.totalAmountInRange + (range.amountInRange || 0),
        totalContractorShare:
          acc.totalContractorShare + (range.contractorShare || 0),
      }),
      { totalAmountInRange: 0, totalContractorShare: 0 }
    );

    let ltAmount = 0,
      ltShare = 0,
      gtAmount = 0,
      gtShare = 0;

    if (targetPrice && actualCostPct !== undefined) {
      if (actualCostPct < ranges.lessThanRange.percentage) {
        ltAmount =
          ((ranges.lessThanRange.percentage - actualCostPct) / 100) *
          targetPrice;
        ltShare = ltAmount * (ranges.lessThanRange.sharePercentage / 100);
      }

      if (actualCostPct > ranges.greaterThanRange.percentage) {
        gtAmount =
          ((actualCostPct - ranges.greaterThanRange.percentage) / 100) *
          targetPrice;
        gtShare = gtAmount * (ranges.greaterThanRange.sharePercentage / 100);
      }
    }

    return {
      totalAmountInRange:
        dynamicTotals.totalAmountInRange + ltAmount + gtAmount,
      totalContractorShare:
        dynamicTotals.totalContractorShare + ltShare + gtShare,
      lessThanAmount: ltAmount,
      lessThanShare: ltShare,
      greaterThanAmount: gtAmount,
      greaterThanShare: gtShare,
    };
  }, [ranges, targetPrice, actualCostPct]);

  const handleFixedRangeChange = useCallback(
    (
      type: 'less-than' | 'greater-than',
      field: 'percentage' | 'sharePercentage',
      value: string
    ) => {
      const numValue = Math.max(0, Math.min(1000, Number(value) || 0));

      // Update first dynamic range when changing less-than percentage
      if (
        type === 'less-than' &&
        field === 'percentage' &&
        ranges.dynamicRanges[0]
      ) {
        const updatedDynamic = ranges.dynamicRanges.map((r, i) =>
          i === 0 ? { ...r, startPercentage: numValue } : r
        );

        onChange({
          ...ranges,
          lessThanRange: { ...ranges.lessThanRange, [field]: numValue },
          dynamicRanges: updatedDynamic,
        });
        return;
      }

      onChange({
        ...ranges,
        [type === 'less-than' ? 'lessThanRange' : 'greaterThanRange']: {
          ...ranges[
            type === 'less-than' ? 'lessThanRange' : 'greaterThanRange'
          ],
          [field]: numValue,
        },
      });
    },
    [ranges, onChange]
  );

  const handleDynamicRangeChange = useCallback(
    (id: string, field: keyof Omit<DynamicRange, 'id'>, value: string) => {
      const numValue =
        field === 'sharePercentage'
          ? Math.max(0, Math.min(100, Number(value) || 0))
          : Math.max(0, Math.min(1000, Number(value) || 0));

      const currentIndex = ranges.dynamicRanges.findIndex((r) => r.id === id);
      const updatedRanges = [...ranges.dynamicRanges];
      const currentRange = { ...updatedRanges[currentIndex], [field]: numValue };

      // Maintain continuity when changing end percentage
      if (
        field === 'endPercentage' &&
        currentIndex < updatedRanges.length - 1
      ) {
        updatedRanges[currentIndex + 1] = {
          ...updatedRanges[currentIndex + 1],
          startPercentage: numValue,
        };
      }

      updatedRanges[currentIndex] = currentRange;
      onChange({ ...ranges, dynamicRanges: updatedRanges });
    },
    [ranges, onChange]
  );

  const addDynamicRange = useCallback(() => {
    const lastRange = ranges.dynamicRanges[ranges.dynamicRanges.length - 1];
    const start = lastRange
      ? lastRange.endPercentage
      : ranges.lessThanRange.percentage;

    const newRange: DynamicRange = {
      id: crypto.randomUUID(),
      startPercentage: start,
      endPercentage: start + 10,
      sharePercentage: 50,
      amountInRange: 0,
      contractorShare: 0,
    };

    onChange({
      ...ranges,
      dynamicRanges: [...ranges.dynamicRanges, newRange],
    });
  }, [ranges, onChange]);

  const removeDynamicRange = useCallback(
    (id: string) => {
      onChange({
        ...ranges,
        dynamicRanges: ranges.dynamicRanges.filter((range) => range.id !== id),
      });
    },
    [ranges, onChange]
  );

  return {
    totals,
    handleFixedRangeChange,
    handleDynamicRangeChange,
    addDynamicRange,
    removeDynamicRange,
  };
}
